import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { StatusChip } from '../components/ui/StatusChip';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { IconDownload, IconInbox, IconStore } from '../components/ui/icons';
import { ProvenancePanel } from '../components/ProvenancePanel';
import { assets, API_BASE } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { assetStateToChip } from '../lib/tokens';
import type { AssetSummary } from '../lib/types';
import { pseudoPeaks } from '../components/waveform/model';

function WaveThumb({ seed }: { seed: string }) {
  const peaks = pseudoPeaks(seed, 48);
  return (
    <div className="flex h-12 items-center gap-px" aria-hidden>
      {peaks.map((p, i) => (
        <span key={i} className="w-full bg-wave-rest" style={{ height: `${Math.max(8, p * 100)}%` }} />
      ))}
    </div>
  );
}

function AssetCard({ asset }: { asset: AssetSummary }) {
  const { t, fmt } = useI18n();
  const [showProvenance, setShowProvenance] = useState(false);
  const chip = assetStateToChip(asset.processing_state);
  const ready = asset.processing_state === 'ready';
  const meta = t.library.meta(fmt.duration(asset.duration_ms), fmt.bpm(asset.bpm) || '—', asset.kind.replace('_', ' ').toUpperCase());

  return (
    <Card className="flex flex-col gap-3 p-3">
      <WaveThumb seed={asset.id} />
      <h3 className="type-heading-2 text-primary truncate" title={asset.title}>
        {asset.title}
      </h3>
      <p className="type-data text-secondary tnum">{meta}</p>
      <div className="flex items-center justify-between">
        <StatusChip tone={chip} />
        <span className="type-caption text-muted">{fmt.date(asset.created_at)}</span>
      </div>
      {ready ? (
        <div className="mt-1 flex flex-wrap gap-2">
          <a href={`${API_BASE}/v1/me/licenses/${asset.id}/download?file=pack`} target="_blank" rel="noreferrer" className="grow">
            <Button variant="secondary" size="sm" block iconLeft={<IconDownload size={16} />}>
              {t.library.downloadPack}
            </Button>
          </a>
          <Link to={`/listings/new?asset=${asset.id}`} className="grow">
            <Button variant="primary" size="sm" block iconLeft={<IconStore size={16} />}>
              {t.library.createListing}
            </Button>
          </Link>
        </div>
      ) : null}
      <button
        type="button"
        className="focus-ring type-label text-secondary hover:text-primary text-left"
        aria-expanded={showProvenance}
        onClick={() => setShowProvenance((v) => !v)}
      >
        {showProvenance ? t.library.hideProvenance : t.library.showProvenance}
      </button>
      {showProvenance ? <ProvenancePanel assetId={asset.id} /> : null}
    </Card>
  );
}

export function AssetLibrary() {
  const { t } = useI18n();
  const [items, setItems] = useState<AssetSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(
    async (nextCursor: string | null) => {
      try {
        const page = await assets.mine({ limit: 20, cursor: nextCursor ?? undefined });
        setItems((prev) => (nextCursor ? [...prev, ...page.items] : page.items));
        setCursor(page.next_cursor);
        setDone(!page.next_cursor);
        setError(null);
      } catch (err) {
        setError(apiErrorMessage(err, t));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  if (loading) {
    return (
      <div>
        <PageHeader title={t.library.title} subtitle={t.library.subtitle} />
        <SkeletonGrid count={6} />
      </div>
    );
  }

  if (!error && items.length === 0) {
    return (
      <div>
        <PageHeader title={t.library.title} subtitle={t.library.subtitle} />
        <EmptyState
          icon={<IconInbox size={48} />}
          title={t.library.emptyTitle}
          body={t.library.emptyBody}
          action={
            <Link to="/studio">
              <Button size="lg">{t.library.emptyCta}</Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t.library.title} subtitle={t.library.subtitle} />
      {error ? (
        <p className="type-body-sm text-danger mb-4" role="alert">
          {error}
        </p>
      ) : null}
      <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
        {items.map((asset) => (
          <AssetCard key={asset.id} asset={asset} />
        ))}
      </div>
      {!done ? (
        <div className="mt-6 flex justify-center">
          <Button
            variant="ghost"
            size="md"
            loading={loadingMore}
            onClick={() => {
              setLoadingMore(true);
              void load(cursor);
            }}
          >
            {t.common.loadingMore}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
